/**
 * Reading a source file's animation data without loading it into a scene.
 *
 * A clip-binding source (`TRACE_animation_binding` §2) is used for its
 * animations only — its nodes, meshes and materials exist to carry names and
 * to give standalone viewers a preview ("source rest data is ignored"). So
 * the binder never instantiates it: it reads the GLB's JSON and BIN chunk and
 * decodes the sampler accessors itself. That is also what keeps a source that
 * no node references from rendering anything.
 *
 * Pure: no Babylon. The only I/O is the caller-supplied fetch for an external
 * `buffers[].uri`.
 */

type JsonObject = Record<string, unknown>;

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;
const CHUNK_BIN = 0x004e4942;

/** A parsed glTF file: its JSON and, for a GLB, the embedded BIN chunk. */
export interface GltfContents {
    json: JsonObject;
    /** The GLB BIN chunk (buffer 0 when it has no `uri`), or null. */
    bin: Uint8Array | null;
}

/** Parse a GLB or a UTF-8 `.gltf`. Throws on anything else. */
export function readGltf(bytes: Uint8Array): GltfContents {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.byteLength >= 12 && view.getUint32(0, true) === GLB_MAGIC) {
        let offset = 12;
        let json: JsonObject | null = null;
        let bin: Uint8Array | null = null;
        while (offset + 8 <= bytes.byteLength) {
            const length = view.getUint32(offset, true);
            const type = view.getUint32(offset + 4, true);
            const body = bytes.subarray(offset + 8, offset + 8 + length);
            if (type === CHUNK_JSON && !json) json = JSON.parse(new TextDecoder().decode(body)) as JsonObject;
            else if (type === CHUNK_BIN && !bin) bin = body;
            offset += 8 + length;
        }
        if (!json) throw new Error('GLB has no JSON chunk');
        return { json, bin };
    }
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not a glTF file');
    return { json: parsed as JsonObject, bin: null };
}

function asObject(value: unknown): JsonObject | undefined {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : undefined;
}

function decodeDataUri(uri: string): Uint8Array | null {
    const m = /^data:[^;,]*(;base64)?,(.*)$/s.exec(uri);
    if (!m) return null;
    if (!m[1]) return new TextEncoder().encode(decodeURIComponent(m[2]));
    const text = atob(m[2]);
    const out = new Uint8Array(text.length);
    for (let i = 0; i < text.length; i++) out[i] = text.charCodeAt(i);
    return out;
}

/**
 * Every `buffers[i]` of the file as bytes: the GLB BIN chunk, a `data:` URI,
 * or whatever `fetchUri` returns for an external one (null when it cannot).
 */
export async function loadGltfBuffers(
    contents: GltfContents,
    fetchUri?: (uri: string) => Promise<Uint8Array | null>,
): Promise<(Uint8Array | null)[]> {
    const buffers = Array.isArray(contents.json.buffers) ? contents.json.buffers : [];
    return Promise.all(buffers.map(async (b, index) => {
        const uri = asObject(b)?.uri;
        if (typeof uri !== 'string') return index === 0 ? contents.bin : null;
        if (uri.startsWith('data:')) return decodeDataUri(uri);
        return fetchUri ? fetchUri(uri) : null;
    }));
}

const COMPONENTS: Record<string, number> = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT2: 4, MAT3: 9, MAT4: 16 };
const COMPONENT_BYTES: Record<number, number> = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 };

function readComponent(view: DataView, offset: number, componentType: number, normalized: boolean): number {
    switch (componentType) {
        case 5120: { const v = view.getInt8(offset); return normalized ? Math.max(v / 127, -1) : v; }
        case 5121: { const v = view.getUint8(offset); return normalized ? v / 255 : v; }
        case 5122: { const v = view.getInt16(offset, true); return normalized ? Math.max(v / 32767, -1) : v; }
        case 5123: { const v = view.getUint16(offset, true); return normalized ? v / 65535 : v; }
        case 5125: return view.getUint32(offset, true);
        case 5126: return view.getFloat32(offset, true);
        default: throw new Error(`unknown componentType ${componentType}`);
    }
}

/** Read `count × components` values from a bufferView into `out`. */
function readInto(
    json: JsonObject, buffers: readonly (Uint8Array | null)[],
    viewIndex: number, byteOffset: number, count: number, components: number,
    componentType: number, normalized: boolean, out: Float32Array | Uint32Array, packedStride?: boolean,
): void {
    const bufferView = asObject((json.bufferViews as unknown[] | undefined)?.[viewIndex]);
    if (!bufferView) throw new Error(`bufferView ${viewIndex} does not exist`);
    const buffer = buffers[bufferView.buffer as number];
    if (!buffer) throw new Error(`buffer ${String(bufferView.buffer)} is not available`);
    const size = COMPONENT_BYTES[componentType];
    if (!size) throw new Error(`unknown componentType ${componentType}`);
    const elementSize = size * components;
    const stride = !packedStride && typeof bufferView.byteStride === 'number' && bufferView.byteStride > 0
        ? bufferView.byteStride
        : elementSize;
    const base = (typeof bufferView.byteOffset === 'number' ? bufferView.byteOffset : 0) + byteOffset;
    const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    for (let e = 0; e < count; e++) {
        for (let c = 0; c < components; c++) {
            out[e * components + c] = readComponent(view, base + e * stride + c * size, componentType, normalized);
        }
    }
}

/**
 * Decode accessor `index` to floats (normalized integers dequantized per
 * glTF 2.0 §3.11), honouring `byteStride`, an absent `bufferView` (zeros)
 * and `sparse` substitution.
 */
export function readAccessor(
    json: JsonObject,
    buffers: readonly (Uint8Array | null)[],
    index: number,
): { data: Float32Array; count: number; components: number } {
    const accessor = asObject((json.accessors as unknown[] | undefined)?.[index]);
    if (!accessor) throw new Error(`accessor ${index} does not exist`);
    const components = COMPONENTS[accessor.type as string];
    const count = accessor.count as number;
    if (!components || !Number.isInteger(count) || count < 0) throw new Error(`accessor ${index} is malformed`);
    const componentType = accessor.componentType as number;
    const normalized = accessor.normalized === true;
    const data = new Float32Array(count * components);
    if (Number.isInteger(accessor.bufferView)) {
        readInto(json, buffers, accessor.bufferView as number,
            typeof accessor.byteOffset === 'number' ? accessor.byteOffset : 0,
            count, components, componentType, normalized, data);
    }
    const sparse = asObject(accessor.sparse);
    if (sparse && Number.isInteger(sparse.count) && (sparse.count as number) > 0) {
        const n = sparse.count as number;
        const indices = asObject(sparse.indices) ?? {};
        const values = asObject(sparse.values) ?? {};
        const where = new Uint32Array(n);
        readInto(json, buffers, indices.bufferView as number,
            typeof indices.byteOffset === 'number' ? indices.byteOffset : 0,
            n, 1, indices.componentType as number, false, where, true);
        const replacement = new Float32Array(n * components);
        readInto(json, buffers, values.bufferView as number,
            typeof values.byteOffset === 'number' ? values.byteOffset : 0,
            n, components, componentType, normalized, replacement, true);
        for (let k = 0; k < n; k++) {
            data.set(replacement.subarray(k * components, (k + 1) * components), where[k] * components);
        }
    }
    return { data, count, components };
}

/** One decoded animation sampler. */
export interface DecodedSampler {
    /** Key times, seconds. */
    input: Float32Array;
    /** Output values, `input.length × stride` (× 3 for CUBICSPLINE: in-tangent, value, out-tangent). */
    output: Float32Array;
    interpolation: 'LINEAR' | 'STEP' | 'CUBICSPLINE';
}

/** Decode sampler `samplerIndex` of animation `animationIndex`. */
export function readSampler(
    json: JsonObject,
    buffers: readonly (Uint8Array | null)[],
    animationIndex: number,
    samplerIndex: number,
): DecodedSampler {
    const animation = asObject((json.animations as unknown[] | undefined)?.[animationIndex]);
    const sampler = asObject((animation?.samplers as unknown[] | undefined)?.[samplerIndex]);
    if (!sampler) throw new Error(`animation ${animationIndex} has no sampler ${samplerIndex}`);
    const interpolation = (sampler.interpolation ?? 'LINEAR') as string;
    if (interpolation !== 'LINEAR' && interpolation !== 'STEP' && interpolation !== 'CUBICSPLINE') {
        throw new Error(`sampler interpolation "${interpolation}" is not glTF`);
    }
    return {
        input: readAccessor(json, buffers, sampler.input as number).data,
        output: readAccessor(json, buffers, sampler.output as number).data,
        interpolation,
    };
}
